import React, { useEffect, useState } from "react";
import { GET_ALL_LEAVE_REPORT } from "../../constants";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import LeaveReportDesign from "./LeaveReportDesign";
import dayjs from "dayjs";
import FilterListIcon from "@mui/icons-material/FilterList";

const buttonStyle = {
  border: "1px solid #fff",
  borderRadius: "4px",
  margin: "5px",
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#097969",
  color: "#fff",
  transition: "background-color 0.3s, transform 0.2s",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
};

const hoverEffects = (e, isHovering) => {
  e.currentTarget.style.backgroundColor = isHovering ? "#0B9B73" : "#097969";
  e.currentTarget.style.transform = isHovering ? "scale(1.05)" : "scale(1)";
};

const SupervisorLeaveReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filterShow, setFilterShow] = useState(false);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD")
  );

  const fetchReportData = async (startDate, endDate) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${GET_ALL_LEAVE_REPORT}?startDate=${startDate}&endDate=${endDate}`
      );
      console.log(response.data);

      // Ensure response contains summary data
      setReportData(response.data || { summary: {} });
    } catch (error) {
      console.error("Error fetching data", error);
      setReportData({ summary: {} }); // Ensure reportData always has an empty summary
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(startDate, endDate);
  }, []);

  const handleApply = () => {
    fetchReportData(startDate, endDate); // Fetch data for the selected date range
    setFilterShow(false);
    localStorage.setItem("startDate", startDate); // Save selected dates to localStorage
    localStorage.setItem("endDate", endDate);
  };

  const handleClear = () => {
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    setStartDate(yesterday); // Reset to yesterday
    setEndDate(yesterday); // Reset to yesterday
    fetchReportData(yesterday, yesterday);
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    setFilterShow(false);
    setReportData([]);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh", // Full viewport height
          backgroundColor: "#f9f9f9",
        }}
      >
        <Card
          sx={{
            padding: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            boxShadow: 3,
            maxWidth: 400,
            width: "90%", // Responsive width
          }}
        >
          <CircularProgress size={60} sx={{ color: "#097969" }} />
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
            Fetching data...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we prepare the report for you.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 1,
        }}
      >
        {/* Report Message */}
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: "#097969" }}
          gutterBottom
        >
          Employee Leave Report
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1, // Space between items
          }}
        >
          {filterShow && (
            <Box
              sx={{
                backgroundColor: "#f0f4f8",
                padding: 1,
                borderRadius: 2,
                boxShadow: 3,
                width: "100%",
              }}
            >
              <Grid container spacing={2}>
                {/* Start Date Picker */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Grid>

                {/* End Date Picker */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} sm={3}>
                  <Box sx={{ display: "flex" }}>
                    <Button
                      onClick={handleClear}
                      variant="outlined"
                      color="secondary"
                      sx={{ marginRight: 2 }}
                    >
                      Clear
                    </Button>
                    <Button
                      onClick={handleApply}
                      variant="contained"
                      color="primary"
                    >
                      Apply
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Filter Icon */}
          <div>
            <IconButton
              onClick={() => setFilterShow((prev) => !prev)}
              style={buttonStyle}
              onMouseEnter={(e) => hoverEffects(e, true)}
              onMouseLeave={(e) => hoverEffects(e, false)}
            >
              <FilterListIcon />
            </IconButton>
          </div>
        </Box>
      </Box>

      {reportData && <LeaveReportDesign reportData={reportData} />}
    </>
  );
};

export default SupervisorLeaveReport;
